// Per-device unread alert count (PRVSK-84).
//
// One server-side count (GET /alerts/unread) drives both the Dashboard badge and
// the app icon badge. It is per device, not per user: families share one login,
// and one person reading the alerts must not clear another person's badge.
//
// Device id: inside the Flutter app, the app owns the id (it also registers the
// push token with it) and injects window.FLUTTER_DEVICE_ID after the page loads,
// so we wait briefly for it. Browsers and older app versions (which never inject
// it) fall back to an id kept in localStorage.

import { apiFetch } from './streamApi';

const LS_KEY = 'prvsk_device_id';

function randomId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {}
  // crypto.randomUUID needs a secure context — plain-HTTP LAN boxes don't have one.
  return 'd-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
}

function localDeviceId() {
  try {
    let id = localStorage.getItem(LS_KEY);
    if (!id) { id = randomId(); localStorage.setItem(LS_KEY, id); }
    return id;
  } catch {
    return '';
  }
}

const inFlutterApp = () => !!(window.FlutterBadge || window.FlutterLog);

let _idPromise = null;
export function getDeviceId() {
  if (window.FLUTTER_DEVICE_ID) return Promise.resolve(window.FLUTTER_DEVICE_ID);
  if (!_idPromise) {
    _idPromise = (async () => {
      if (inFlutterApp()) {
        for (let i = 0; i < 20 && !window.FLUTTER_DEVICE_ID; i++) {
          await new Promise(r => setTimeout(r, 100));
        }
        if (window.FLUTTER_DEVICE_ID) return window.FLUTTER_DEVICE_ID;
      }
      return localDeviceId();
    })();
  }
  return _idPromise;
}

// Tell the app the current count so it can set its icon badge. Older apps ignore
// unknown FlutterBadge messages (they only act on "clear"), so this is safe.
function notifyApp(count) {
  try {
    if (window.FlutterBadge) window.FlutterBadge.postMessage(`unread:${count}`);
  } catch {}
}

/** { count, last_seen, oldest_ts, oldest_date } — null on failure. */
export async function fetchUnread() {
  try {
    const id = await getDeviceId();
    if (!id) return null;
    const r = await apiFetch(`/alerts/unread?device_id=${encodeURIComponent(id)}`);
    if (!r.ok) return null;
    const d = await r.json();
    notifyApp(d.count || 0);
    return d;
  } catch {
    return null;
  }
}

// The app calls this on every return to the foreground (whatever page is open)
// to refresh its icon badge from the server count.
if (typeof window !== 'undefined') {
  window.__prvskRefreshUnread = () => { fetchUnread(); };
}

/** Mark alerts seen up to `upto` ("YYYYMMDD_HHMMSS" of the newest alert shown). */
export async function markAlertsSeen(upto) {
  try {
    const id = await getDeviceId();
    if (!id) return null;
    const r = await apiFetch('/alerts/seen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: id, ...(upto ? { upto } : {}) }),
    });
    if (!r.ok) return null;
    const d = await r.json();
    notifyApp(d.count || 0);
    return d;
  } catch {
    return null;
  }
}

/** "YYYYMMDD_HHMMSS" from an alert filename, or "". */
export function stampFromFilename(name) {
  const m = /_(\d{8}_\d{6})/.exec(name || '');
  return m ? m[1] : '';
}

export const badgeLabel = n => (n > 99 ? '99+' : String(n));
